import { useRef } from 'react'
import { cn } from '@/lib/utils'

interface Props {
  /** Chiffre (ou « + », « * », « # ») à ajouter au numéro du composeur. */
  onDigit: (digit: string) => void
  darkMode?: boolean
}

const BUTTONS = [
  { digit: '1', letters: '' },
  { digit: '2', letters: 'ABC' },
  { digit: '3', letters: 'DEF' },
  { digit: '4', letters: 'GHI' },
  { digit: '5', letters: 'JKL' },
  { digit: '6', letters: 'MNO' },
  { digit: '7', letters: 'PQRS' },
  { digit: '8', letters: 'TUV' },
  { digit: '9', letters: 'WXYZ' },
  { digit: '*', letters: '' },
  { digit: '0', letters: '+' },
  { digit: '#', letters: '' },
]

/**
 * Pavé du composeur : replié par défaut sur ordinateur (on tape au clavier),
 * ouvert sur écran tactile. Appui long sur 0 : « + ».
 */
export function DialPad({ onDigit, darkMode = false }: Props) {
  const minuteur = useRef<ReturnType<typeof setTimeout> | null>(null)
  const appuiLong = useRef(false)
  // Un toucher déclenche aussi les événements souris : on n'écoute plus qu'eux ensuite.
  const tactile = useRef(false)

  const debut = (digit: string) => {
    appuiLong.current = false
    minuteur.current = setTimeout(() => {
      appuiLong.current = true
      if (digit === '0') onDigit('+')
    }, 500)
  }

  const fin = (digit: string) => {
    if (minuteur.current) clearTimeout(minuteur.current)
    minuteur.current = null
    if (!appuiLong.current) onDigit(digit)
    appuiLong.current = false
  }

  const annuler = () => {
    if (minuteur.current) clearTimeout(minuteur.current)
    minuteur.current = null
  }

  return (
    <div className="grid grid-cols-3 gap-3 place-items-center">
      {BUTTONS.map((btn) => (
        <button
          key={btn.digit}
          type="button"
          // Le champ garde le focus : on continue de taper au clavier après un clic.
          onMouseDown={(e) => {
            e.preventDefault()
            if (!tactile.current) debut(btn.digit)
          }}
          onMouseUp={() => !tactile.current && fin(btn.digit)}
          onMouseLeave={annuler}
          onTouchStart={(e) => {
            e.preventDefault()
            tactile.current = true
            debut(btn.digit)
          }}
          onTouchEnd={(e) => {
            e.preventDefault()
            fin(btn.digit)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              onDigit(btn.digit)
            }
          }}
          className={cn(
            'flex flex-col items-center justify-center w-16 h-16 rounded-full transition-all active:scale-95',
            'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
            darkMode
              ? 'bg-slate-800/60 hover:bg-slate-700 border border-slate-700/60 focus-visible:outline-indigo-400'
              : 'bg-slate-50 hover:bg-slate-100 border border-slate-200 focus-visible:outline-slate-900',
          )}
        >
          <span className={cn('text-xl font-semibold leading-none', darkMode ? 'text-white' : 'text-slate-800')}>
            {btn.digit}
          </span>
          {btn.letters && (
            <span className={cn('mt-0.5 text-[9px] font-bold tracking-wider', darkMode ? 'text-slate-500' : 'text-slate-400')}>
              {btn.letters}
            </span>
          )}
        </button>
      ))}
    </div>
  )
}
